

import { useState, useEffect } from "react";
import Modal from "../../components/Modal";
import { formatCurrency, formatCurrencyFull, formatDate, localToday } from "@/lib/data";
import { useToast } from "@/context/ToastContext";
import { X, CreditCard, Building2 } from "lucide-react";
import { apiClient, mastersApi } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";

interface PaymentMethod {
  id: number;
  name: string;
  status: string;
}

interface VendorPayment {
  id: number;
  amount: number;
  payment_date: string;
  paid_at?: string;
  payment_method: string;
  transaction_id?: string;
  notes?: string;
  proof_path?: string;
}

interface VendorItem {
  type: 'vendor' | 'service';
  id: number;
  description: string;
  quantity: number;
  cost_price: number;
  total_amount: number;
}

interface VendorSummary {
  vendor_id: number;
  vendor_name: string;
  total_amount: number;
  paid_amount: number;
  balance: number;
  items: VendorItem[];
  payments: VendorPayment[];
}

interface GiveVendorPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: number;
  vendor: VendorSummary | null;
  onPaymentAdded: () => void;
}

export default function GiveVendorPaymentModal({
  isOpen,
  onClose,
  eventId,
  vendor,
  onPaymentAdded,
}: GiveVendorPaymentModalProps) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  
  const [formData, setFormData] = useState({
    amount: "",
    payment_method: "",
    payment_date: localToday(),
    transaction_id: "",
    notes: "",
    proof_file: null as File | null,
  });

  // Load payment methods from database
  useEffect(() => {
    if (isOpen) {
      mastersApi.paymentMethods.list({ status: 'active' })
        .then((res: any) => {
          const methods = res.data?.data || res.data || [];
          if (methods.length > 0) {
            setPaymentMethods(methods);
            setFormData(prev => ({ 
              ...prev, 
              payment_method: methods[0].name 
            }));
          }
        })
        .catch(() => {
          // Fallback to defaults if API fails
          setPaymentMethods([
            { id: 1, name: 'Cash', status: 'active' },
            { id: 2, name: 'Card', status: 'active' },
            { id: 3, name: 'Online', status: 'active' },
            { id: 4, name: 'Bank Transfer', status: 'active' },
          ]);
          setFormData(prev => ({ ...prev, payment_method: 'Cash' }));
        });
    }
  }, [isOpen]);

  // Set amount when vendor changes
  useEffect(() => {
    if (vendor && isOpen) {
      setFormData(prev => ({
        ...prev,
        amount: vendor.balance > 0 ? vendor.balance.toString() : "0",
      }));
    }
  }, [vendor, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!vendor) {
      showToast("Please select a vendor", "error");
      return;
    }

    const amount = parseFloat(formData.amount);
    if (!amount || amount <= 0) {
      showToast("Please enter a valid amount", "error");
      return;
    }

    if (amount > vendor.balance) {
      showToast(`Amount cannot exceed balance of ${formatCurrency(vendor.balance)}`, "error");
      return;
    }

    // Ensure required fields
    const paymentMethod = formData.payment_method || paymentMethods[0]?.name || 'Cash';
    const paymentDate = formData.payment_date || localToday();

    // Get event_vendor_id from vendor items
    const vendorItem = vendor.items?.find(item => item.type === 'vendor');
    const eventVendorId = vendorItem?.id;
    const vendorId = vendor.vendor_id;

    setLoading(true);
    try {
      const formDataToSend = new FormData();
      
      if (eventVendorId) {
        formDataToSend.append('event_vendor_id', eventVendorId.toString());
      } else {
        formDataToSend.append('vendor_id', vendorId.toString());
      }
      
      formDataToSend.append('amount', amount.toString());
      formDataToSend.append('payment_method', paymentMethod);
      formDataToSend.append('payment_date', paymentDate);
      
      if (formData.transaction_id) {
        formDataToSend.append('transaction_id', formData.transaction_id);
      }
      if (formData.notes) {
        formDataToSend.append('notes', formData.notes);
      }
      if (formData.proof_file) {
        formDataToSend.append('proof_file', formData.proof_file);
      }

      // Debug: Log request details
      console.log("[Payment] Sending request:", {
        eventId,
        amount: amount.toString(),
        paymentMethod,
        paymentDate,
        hasProofFile: !!formData.proof_file,
        proofFileName: formData.proof_file?.name,
        proofFileSize: formData.proof_file?.size,
        proofFileType: formData.proof_file?.type,
      });

      const response = await apiClient.postFormData(`/events/${eventId}/vendor-payments`, formDataToSend);

      if (response.success) {
        showToast(`Payment of ${formatCurrency(amount)} recorded successfully`, "success");
        setFormData({
          amount: "",
          payment_method: paymentMethods[0]?.name || 'Cash',
          payment_date: localToday(),
          transaction_id: "",
          notes: "",
          proof_file: null,
        });
        onPaymentAdded();
        onClose();
      } else {
        showToast(response.message || "Failed to add payment", "error");
      }
    } catch (error: any) {
      console.error("Payment error:", error);
      // Try to extract detailed validation errors
      let msg = error?.message || "Failed to add payment";
      if (error?.response?.data?.errors) {
        const errors = error.response.data.errors;
        const errorMessages = Object.entries(errors)
          .map(([field, msgs]: [string, any]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
          .join(' | ');
        msg = `Validation Error: ${errorMessages}`;
      }
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const getPaymentDate = (payment: VendorPayment) => {
    return payment.payment_date || payment.paid_at || new Date().toISOString();
  };

  if (!vendor) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Pay ${vendor.vendor_name}`} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Vendor Summary */}
        <div className="bg-stone-50 rounded-lg p-3 border border-stone-200">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="w-4 h-4 text-blue-500" />
            <span className="font-semibold text-stone-800">{vendor.vendor_name}</span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            <div>
              <p className="text-stone-500">To Pay</p>
              <p className="font-bold text-stone-800">{formatCurrency(vendor.total_amount)}</p>
            </div>
            <div>
              <p className="text-stone-500">Paid</p>
              <p className="font-bold text-emerald-600">{formatCurrency(vendor.paid_amount)}</p>
            </div>
            <div>
              <p className="text-stone-500">Balance</p>
              <p className="font-bold text-amber-600">{formatCurrency(vendor.balance)}</p>
            </div>
          </div>
        </div>

        {/* Payment History */}
        {vendor.payments && vendor.payments.length > 0 && (
          <div className="bg-white rounded-lg p-3 border border-stone-200">
            <p className="text-[10px] font-semibold text-stone-500 uppercase mb-2">Previous Payments</p>
            <div className="space-y-1 max-h-24 overflow-y-auto">
              {vendor.payments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between text-xs py-1 border-b border-stone-100 last:border-0">
                  <span className="text-stone-500">{formatDate(getPaymentDate(payment))}</span>
                  <span className="font-semibold text-emerald-600">{formatCurrencyFull(payment.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Amount */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Amount to Pay *</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 font-semibold">₹</span>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={vendor.balance}
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="w-full pl-8 pr-3 py-3 text-lg font-semibold border border-stone-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="0.00"
              required
            />
          </div>
        </div>

        {/* Payment Method & Date */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Payment Method *</label>
            <select
              value={formData.payment_method}
              onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
              className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            >
              {paymentMethods.map((method) => (
                <option key={method.id} value={method.name}>{method.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Payment Date *</label>
            <input
              type="date"
              value={formData.payment_date}
              onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
              className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
        </div>

        {/* Transaction ID */}
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Transaction / Reference ID</label>
          <input
            type="text"
            value={formData.transaction_id}
            onChange={(e) => setFormData({ ...formData, transaction_id: e.target.value })}
            className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Optional reference number"
          />
        </div>

        {/* Proof Upload */}
        <ImageUpload
          selectedFile={formData.proof_file}
          onFileSelect={(file) => setFormData({ ...formData, proof_file: file })}
          maxSize={2 * 1024 * 1024}
          compressToWebP={true}
        />

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 px-4 text-sm font-medium border border-stone-300 text-stone-600 rounded-lg hover:bg-stone-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-3 px-4 text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <CreditCard className="w-4 h-4" />
                Pay {formatCurrency(parseFloat(formData.amount) || 0)}
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
